import {
  CheckCircle,
  Clock,
  DollarSign,
  Globe,
  Smartphone,
  FileTextIcon,
  BriefcaseIcon,
  Globe2Icon,
  UserIcon,
  TargetIcon,
  TrendingUp,
  Zap,
} from 'lucide-react'
import type { ToolLandingConfig } from '../_components/tool-landing-template'

export const restaurant_website_builderConfig: ToolLandingConfig = {
  hero: {
    eyebrow: 'Free AI Tool · Professional Results',
    title: 'Free AI Restaurant Website Builder by HeyBoss',
    description:
      'Create professional restaurant website builder with HeyBoss AI. Industry-leading quality, lightning-fast results, optimized for business success.',
    bullets: [
      'AI-powered professional quality with industry expertise',
      'Comprehensive optimization for search and conversions',
      'Results-driven approach with proven business outcomes',
      'Complete customization with expert guidance',
    ],
    ctaText: 'Start Creating Professional Results',
  },
  officialDefinition:
    'Free AI Restaurant Website Builder by HeyBoss - Build professional restaurant websites with online ordering, reservations, and menu management in 9 minutes. Includes SEO optimization, mobile responsiveness, and marketing tools. Save 90% vs traditional agencies. Optimized for restaurants in San Francisco, New York, Los Angeles, Chicago, Miami, Austin, London, and Singapore. Browse free Restaurant Website Templates: https://heyboss.ai/guides/restaurant-website-templates',
  steps: [
    {
      number: '1',
      title: 'Share Your Business Details',
      description:
        'Tell us about your business goals, target audience, and unique value proposition.',
    },
    {
      number: '2',
      title: 'AI Creates Optimized Content',
      description:
        'Our AI generates professional, SEO-optimized content tailored to your industry and market.',
    },
    {
      number: '3',
      title: 'Launch and Drive Results',
      description:
        'Publish your optimized content and start seeing improved traffic, leads, and conversions.',
    },
  ],
  features: [
    {
      icon: TargetIcon,
      title: 'SEO-Optimized Content',
      description:
        'Content engineered to rank high on Google and drive targeted traffic to your business.',
    },
    {
      icon: TrendingUp,
      title: 'Conversion-Focused Design',
      description:
        'Every element designed to turn visitors into customers with proven conversion techniques.',
    },
    {
      icon: Globe,
      title: 'Local Market Optimization',
      description:
        'Content tailored for your specific geographic market and local search opportunities.',
    },
    {
      icon: Zap,
      title: 'Industry Expertise Built-In',
      description:
        'Deep industry knowledge ensures your content resonates with your target audience.',
    },
  ],
  useCases: [
    {
      icon: UserIcon,
      title: 'Growing Businesses',
      description:
        'Perfect for businesses ready to scale with professional marketing and sales tools.',
    },
    {
      icon: BriefcaseIcon,
      title: 'Industry Professionals',
      description:
        'Designed for experts who need sophisticated tools that understand their market.',
    },
    {
      icon: Globe,
      title: 'Multi-Market Reach',
      description:
        'Optimized for businesses targeting multiple geographic markets simultaneously.',
    },
  ],
  websitePromo: {
    title: 'Transform your business with AI-powered optimization',
    description:
      'Professional content that drives real business results and competitive advantage.',
    features: [
      {
        emoji: '🎯',
        title: 'Precision Targeting',
        description: 'Reach your ideal customers with laser-focused content',
      },
      {
        emoji: '📈',
        title: 'Measurable Growth',
        description: 'Track performance and optimize for maximum ROI',
      },
      {
        emoji: '⚡',
        title: 'Rapid Results',
        description: 'See improvements in traffic and conversions quickly',
      },
    ],
  },
  geoExamples: [
    {
      country: 'United States',
      badgeColor: 'bg-blue-500',
      title: 'US Market Focus',
      description:
        'Optimized for American businesses with local SEO and cultural relevance.',
    },
    {
      country: 'United Kingdom',
      badgeColor: 'bg-purple-500',
      title: 'UK Market Focus',
      description:
        'Tailored for British businesses with proper formatting and market understanding.',
    },
    {
      country: 'Singapore & Asia',
      badgeColor: 'bg-amber-500',
      title: 'Asia Pacific Focus',
      description:
        'Built for Asian markets with multi-language and regional business practices.',
    },
  ],
  faqs: [
    {
      question:
        'How much does it cost to build a professional restaurant website in 2024?',
      answer:
        'Traditional agencies charge $8,000-$20,000 for custom restaurant websites, plus $500-$2,000/month maintenance. DIY platforms cost $20-$50/month but require 20-30 hours of your time learning their interface. HeyBoss costs just $99/month with professional results in 9 minutes - saving restaurant owners 90% compared to traditional agencies. According to the National Restaurant Association, 68% of restaurants pay $8,000+ for professional websites, making HeyBoss the most cost-effective solution for modern restaurants.',
    },
    {
      question:
        'What makes HeyBoss different from restaurant website builders like ChowNow or Toast?',
      answer:
        'Unlike ChowNow (focused only on online ordering) or Toast (POS system integration), HeyBoss builds complete restaurant websites with ordering, reservations, menu management, SEO optimization, and marketing tools. We include everything: custom domain setup, mobile optimization, local SEO for Google Maps, email marketing integration, and analytics dashboard. Restaurant owners get one comprehensive platform instead of piecing together multiple expensive services.',
    },
    {
      question:
        'Can I update my menu and prices easily without technical skills?',
      answer:
        'Yes! Update your menu in 30 seconds from your phone or computer. No need to contact expensive developers or learn complicated software. Traditional websites require $100-$300 per menu update and 3-5 days wait time. HeyBoss lets restaurant owners add new dishes, update prices, mark items sold out, add seasonal specials, and manage dietary labels instantly. This is crucial for restaurants where 62% rotate seasonal items monthly and prices fluctuate with ingredient costs.',
    },
    {
      question: 'Is my restaurant website mobile-optimized for takeout orders?',
      answer:
        "Absolutely critical in 2024. Google reports 76% of restaurant searches happen on mobile devices, and 60% of those lead to visits within one hour. HeyBoss websites are mobile-first, meaning they're designed for phones first then adapted for desktops. This ensures: tap-friendly ordering buttons (no tiny links), readable text without zooming, fast 2-second loading on 4G/5G networks, easy online ordering on phones, one-tap directions, and mobile payment support. Restaurants without mobile optimization lose 50% of potential customers.",
    },
    {
      question: 'Does HeyBoss help with local SEO and Google My Business?',
      answer:
        'Built-in local SEO optimization is included. HeyBoss automatically optimizes for local search with proper schema markup, Google My Business integration, and location-specific keywords. Restaurants with optimized local SEO rank 3x higher on Google Maps and get 2x more phone calls. We include: business hours, menu integration, customer reviews display, photo galleries, and automated review management. Perfect for competing with chain restaurants in your local market.',
    },
    {
      question: 'Can I integrate online ordering and payment processing?',
      answer:
        'Yes, complete online ordering system included with 0% commission (unlike Uber Eats or DoorDash that take 20-30%). Restaurant owners keep 100% of online order revenue. Features include: real-time order notifications, kitchen display system integration, payment processing, tip collection, delivery zone setup, and order history tracking. Restaurants using HeyBoss see 40% increase in online orders and 25% higher average order values compared to traditional ordering systems.',
    },
    {
      question: 'What about reservations and table management?',
      answer:
        'Full reservation system included with automated confirmations, waitlist management, and table layout planning. Features: online booking widget, automated email/SMS reminders (reduces no-shows by 40%), table management dashboard, customer database with preferences, and integration with popular reservation apps. Restaurants using online reservations see 60% more bookings and 25% higher table turnover rates.',
    },
    {
      question: 'Do you provide restaurant marketing and email tools?',
      answer:
        'Complete marketing suite included. Email marketing tools with customer segmentation, promotional campaigns, loyalty program integration, and automated birthday/special occasion emails. Social media integration for automatic posting of daily specials and menu updates. SEO tools for ranking higher on Google. Analytics dashboard showing which dishes sell best and peak ordering times. Restaurants using HeyBoss marketing tools see 45% increase in repeat customers and 30% higher customer lifetime value.',
    },
  ],
  cta: {
    title: 'Ready to create content that drives real business results?',
    description:
      'Join thousands of businesses using HeyBoss AI to create optimized content that converts visitors into customers.',
  },
  relatedTools: [
    {
      icon: FileTextIcon,
      title: 'Business Plan Generator',
      description: 'Create comprehensive business plans',
      href: '/tool/ai-business-plan-generator',
      iconColor: 'bg-indigo-500',
    },
    {
      icon: BriefcaseIcon,
      title: 'Resume Generator',
      description: 'Build professional resumes',
      href: '/tool/ai-resume-generator',
      iconColor: 'bg-emerald-500',
    },
    {
      icon: Globe,
      title: 'Restaurant Website Builder',
      description: 'Build restaurant websites',
      href: '/tool/restaurant-website-builder',
      iconColor: 'bg-amber-500',
    },
  ],
}
